import React from 'react';
import { Alert, Button, Divider, Form, Input, Select, Space, Tag, Typography } from 'antd';
import { BankOutlined, GoogleOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GoogleSignInButton from './GoogleSignInButton';
import CodeApporteurField from '../../../components/parrainage/CodeApporteurField';
import { useReferential } from '../../../context/ReferentialContext';
import { signupPalette, signupGradient } from './signupTheme';
import type { SignupPlan } from '../../../api/auth/signup';

const { Text } = Typography;

type Props = {
    plan: SignupPlan;
    email: string;
    onEmailChange: (value: string) => void;
    googleEmail?: string;
    googleClientId?: string;
    onGoogleCredential: (idToken: string) => void;
    onClearGoogle: () => void;
    onContinue: () => void;
    submitting: boolean;
    freemiumChildrenCount: number;
    error?: string;
    /** Pays — demandé ici en freemium ; en premium il l'est à l'étape paiement. */
    showCountry?: boolean;
    country?: string;
    onCountryChange?: (code: string) => void;
    /**
     * Code de l'animateur qui a recommandé Tilmicode — pré-rempli par son lien
     * de parrainage. Demandé ici, seul écran commun au freemium et au premium.
     * Facultatif, jamais bloquant.
     */
    codeApporteur: string;
    onCodeApporteurChange: (code: string) => void;
};

/**
 * Étape 1 — la seule étape du freemium : une adresse email (ou Google) et le pays.
 * Aucun mot de passe n'est demandé ici : il est défini via le lien reçu par mail.
 */
const StepAccount: React.FC<Props> = ({
    plan,
    email,
    onEmailChange,
    googleEmail,
    googleClientId,
    onGoogleCredential,
    onClearGoogle,
    onContinue,
    submitting,
    freemiumChildrenCount,
    error,
    showCountry = false,
    country,
    onCountryChange,
    codeApporteur,
    onCodeApporteurChange,
}) => {
    const { t } = useTranslation();
    const { countries, isReferentialLoaded } = useReferential();

    const isFreemium = plan === 'freemium';
    const identityReady = Boolean(googleEmail) || /^\S+@\S+\.\S+$/.test(email.trim());
    const canContinue = identityReady && (!showCountry || Boolean(country));

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {error && <Alert type="error" showIcon message={error} />}

            {googleEmail ? (
                <div
                    style={{
                        border: `1px solid ${signupPalette.border}`,
                        borderRadius: 14,
                        padding: '16px 18px',
                        background: signupPalette.background,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <Space>
                        <GoogleOutlined style={{ fontSize: 20, color: signupPalette.primary }} />
                        <div>
                            <Text style={{ display: 'block', fontWeight: 600, color: signupPalette.textPrimary }}>
                                {googleEmail}
                            </Text>
                            <Tag color="green" style={{ marginTop: 4 }}>
                                {t('signup.account.googleVerified', 'Adresse vérifiée par Google')}
                            </Tag>
                        </div>
                    </Space>
                    <Button type="link" onClick={onClearGoogle} style={{ padding: 0 }}>
                        {t('signup.account.changeAccount', 'Changer')}
                    </Button>
                </div>
            ) : (
                <>
                    <Form layout="vertical" requiredMark={false} onFinish={onContinue}>
                        <Form.Item
                            label={<Text style={{ fontWeight: 500 }}>{t('signup.account.emailLabel', 'Votre adresse email')}</Text>}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                autoFocus
                                size="large"
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => onEmailChange(e.target.value)}
                                onPressEnter={() => canContinue && onContinue()}
                                prefix={<MailOutlined style={{ color: signupPalette.textMuted }} />}
                                placeholder={t('signup.account.emailPlaceholder', 'prenom@exemple.com')}
                                style={{ borderRadius: 12, padding: '12px 16px', fontSize: 15 }}
                            />
                        </Form.Item>
                    </Form>

                    {googleClientId && (
                        <>
                            <Divider plain style={{ margin: '4px 0', color: signupPalette.textMuted }}>
                                {t('signup.account.or', 'ou')}
                            </Divider>
                            <GoogleSignInButton clientId={googleClientId} onCredential={onGoogleCredential} text="signup_with" />
                        </>
                    )}
                </>
            )}

            {showCountry && (
                <div>
                    <Text style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
                        {t('signup.account.countryLabel', 'Votre pays')}
                    </Text>
                    <Select
                        showSearch
                        size="large"
                        style={{ width: '100%' }}
                        value={country || undefined}
                        onChange={(value) => onCountryChange?.(value)}
                        loading={!isReferentialLoaded}
                        optionFilterProp="label"
                        placeholder={t('signup.account.countryPlaceholder', 'Sélectionnez votre pays')}
                        options={countries.map((c) => ({ value: c.code, label: c.name }))}
                    />
                    <Text style={{ fontSize: 12, color: signupPalette.textSecondary, display: 'block', marginTop: 6 }}>
                        {t('signup.account.countryHint', 'Il nous sert à adapter le contenu et les tarifs.')}
                    </Text>
                </div>
            )}

            <CodeApporteurField value={codeApporteur} onChange={onCodeApporteurChange} />

            {/* Le champ « code de l'école » a été retiré de l'inscription.

                Il n'ouvrait plus aucun droit depuis que le premium est la
                conséquence exclusive d'un paiement confirmé — mais il restait
                visible, et promettait donc quelque chose qui n'arrivait pas.

                Surtout, il n'a plus sa place ICI : une famille dont l'école est
                partenaire a déjà des comptes, créés à l'import. S'inscrire lui
                en créerait de nouveaux — le double compte que cette refonte
                corrige. Le tarif négocié s'applique désormais depuis le menu
                paiement, pour un parent déjà connecté, donc déjà existant. */}
            <Alert
                type="info"
                showIcon
                icon={<BankOutlined />}
                message={t('signup.account.schoolFamilyTitle', "L'école de votre enfant est partenaire ?")}
                description={t(
                    'signup.account.schoolFamilyHint',
                    "Vos comptes ont déjà été créés par l'établissement : connectez-vous avec les identifiants qu'il vous a remis, plutôt que d'en créer de nouveaux."
                )}
            />


            <Button
                type="primary"
                size="large"
                block
                loading={submitting}
                disabled={!canContinue}
                onClick={onContinue}
                style={{
                    height: 52,
                    borderRadius: 12,
                    border: 'none',
                    background: signupGradient,
                    fontWeight: 600,
                    fontSize: 16,
                }}
            >
                {isFreemium
                    ? t('signup.account.submitFreemium', 'Créer mon compte gratuit')
                    : t('signup.actions.continue', 'Continuer')}
            </Button>

            {isFreemium && (
                <Text style={{ color: signupPalette.textSecondary, fontSize: 13, textAlign: 'center', display: 'block' }}>
                    {t(
                        'signup.account.freemiumNote',
                        '{{count}} comptes enfants seront créés automatiquement. Aucune carte bancaire n’est demandée.',
                        { count: freemiumChildrenCount }
                    )}
                </Text>
            )}
        </Space>
    );
};

export default StepAccount;
